import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from 'app/auth/auth.service';
import { EncryptionService } from 'app/services/encryption.service';
import { MatIconModule } from '@angular/material/icon';
import {
    RecaptchaFormsModule,
    RecaptchaModule,
    RecaptchaComponent,
} from 'ng-recaptcha';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputGeneralComponent } from 'app/components/input-general/input-general.component';
import { environment } from 'environments/environment';
import { ApplicationService } from 'app/services/application.service';
import {
    CHANGE_LANGUAGE,
    formatTime,
    getUtmSourceBRPURL,
} from 'app/shared/utils';
import { GtmService } from 'app/services/gtm.service';
import { GlobalToasterComponent } from '../../../components/global-toaster/global-toaster.component';
import { GlobalToasterService } from 'app/services/global-toaster.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ForcePasswordPopupComponent } from 'app/components/force-password-popup/force-password-popup.component';
import { UserDisabledPopupComponent } from 'app/components/user-disabled-popup/user-disabled-popup.component';
import { AccountUnverifiedModalComponent } from 'app/components/account-unverified-modal/account-unverified-modal.component';

@Component({
    selector: 'auth-sign-in',
    templateUrl: './sign-in.component.html',
    standalone: true,
    imports: [
        FormsModule,
        CommonModule,
        RouterModule,
        MatIconModule,
        RecaptchaModule,
        TranslateModule,
        ReactiveFormsModule,
        RecaptchaFormsModule,
        InputGeneralComponent,
        GlobalToasterComponent,
    ],
    styleUrls: ['./sign-in-component.scss'],
    styles: [
        `
            :host {
                width: 100%;
            }
        `,
    ],
})
export class AuthSignInComponent {
    @ViewChild('recaptchaRef') recaptchaRef: RecaptchaComponent;

    isRequired: any;
    patternMatch: any;
    signInForm: FormGroup;

    submitted: boolean = false;
    captchaResolved: boolean = false;
    returnURL: string = '';
    fromIDA: boolean = false;
    calendarEventURL = '';
    brpSourceUrl = '';
    captchaRes: string;
    closeTostr: string;
    siteKey = environment.siteKey;
    showErrorsOnSubmit: boolean = false;
    JSON: JSON;
    showCalendarEventLoginTitle: boolean = false;
    eventData: any;
    dayOfEvent: any;
    dateOfEvent: any;
    errorOnEvent: boolean = false;
    isAuthenticated: boolean;
    selectedLanguage: any;
    isRegistered: any;
    isReset: any;
    eventStartTime: any;
    eventEndTime: any;

    constructor(
        private router: Router,
        private fb: FormBuilder,
        private authService: AuthService,
        public translate: TranslateService,
        private ngxSpinner: NgxSpinnerService,
        private activatedRoute: ActivatedRoute,
        private applicationService: ApplicationService,
        private encryptionService: EncryptionService,
        private gtmService: GtmService,
        public globalToasterSerive: GlobalToasterService,
        private toastr: ToastrService,
        private dialog: MatDialog,
    ) {
        this.JSON = JSON;
    }

    ngOnInit(): void {
        this.selectedLanguage = localStorage.getItem('language');
        this.handleStateFlag('isRegistered');
        this.handleStateFlag('isReset');
        this.signInForm = this.fb.group({
            username: [
                '',
                [Validators.required, Validators.pattern(/^[A-Za-z0-9@._-]+$/)],
            ],
            password: ['', [Validators.required]],
            recaptcha: ['', [Validators.required]],
        });

        this._translate();

        // assigning returnUrl flag for redirection
        this.activatedRoute.queryParams.subscribe((params) => {
            this.returnURL = params.returnUrl || null;
            this.calendarEventURL = params?.calendarEventURL;
            this.brpSourceUrl = params?.brpSourceUrl;
            this.fromIDA = params?.fromIDA === 'true';

            if (params?.lang) {
                localStorage.setItem('language', params?.lang);
                this.translate.reloadLang(params?.lang);
                this.translate.use(params?.lang);
            }

            if (this.calendarEventURL) {
                this.showCalendarEventLoginTitle = true;
                const str = this.calendarEventURL;
                const splitted = str.split('=');
                this.getEventDetails(splitted[1]);
            } else {
                this.showCalendarEventLoginTitle = false;
            }
        });

        this.isAuthenticated =
            localStorage.getItem('isAuthenticated') == 'true' ? true : false;
        if (this.isAuthenticated) {
            const secretEncKey = atob(localStorage.getItem('cepn'));
            let profile = JSON.parse(
                atob(
                    this.encryptionService.decryptData(
                        localStorage.getItem('profile'),
                        secretEncKey,
                    ),
                ),
            );

            // Mirrors onSignIn()'s success path. A user who hits
            // /login?fromIDA=true while already signed in never runs that
            // callback, so without this the 'fromIDA' flag never gets set and
            // chatbot.component.ts's handleReturnFromIdaLogin() never reopens
            // the widget or hands iDA its saved resume state — the redirect
            // silently goes nowhere and the user just retries the same action.
            if (this.fromIDA) {
                localStorage.setItem('fromIDA', 'true');
            }

            if (this.brpSourceUrl) {
                //To check if user is coming from reference/BRP URL
                let utmSource = getUtmSourceBRPURL(this.brpSourceUrl);
                let brpSourceUrl = `/business-financing?utm_source=${utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`;
                this.router.navigateByUrl(brpSourceUrl);
            } else if (this.returnURL) {
                this.router.navigateByUrl(this.returnURL);
            } else {
                // nav to /profile when sms is unverified/verified
                this.router.navigate(['/profile']);
            }
        }
    }

    private _translate() {
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
        });
        this.translate.get('ERROR.PATTERN_MATCH').subscribe((data: any) => {
            this.patternMatch = data;
        });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });
    }

    handleStateFlag(key: 'isRegistered' | 'isReset'): void {
        const verifyButton = document.getElementById(
            'hiddenGAButton',
        ) as HTMLButtonElement;
        if (history.state[key]) {
            this[key] = history.state[key];
            if (key == 'isRegistered') {
                setTimeout(() => {
                    if (verifyButton) {
                        verifyButton.click();
                    }
                }, 5000);
            } else if (key === 'isReset') {
                if (localStorage.getItem('language') == 'en') {
                    this.toastr.success(
                        'Password reset successfully. Login now',
                    );
                } else {
                    this.toastr.success(
                        'Tukar kata laluan berjaya. Log masuk sekarang',
                    );
                }
            }
            setTimeout(() => {
                this[key] = null;
            }, 6000);
        }
    }

    getEventDetails(id: any) {
        this.applicationService.getEventDetails(id).subscribe({
            next: (res) => {
                this.errorOnEvent = false;
                this.eventData = res;
                localStorage.setItem('event_title', this.eventData?.title);

                const dateData = new Date(this.eventData?.date);
                if (localStorage.getItem('language') == 'en') {
                    this.dateOfEvent = this.getDateWithFormat(
                        dateData,
                        'en-us',
                    );
                    this.dayOfEvent = dateData.toLocaleString('en-us', {
                        weekday: 'long',
                    });
                } else {
                    this.dateOfEvent = this.getDateWithFormat(
                        dateData,
                        'ms-MY',
                    );
                    this.dayOfEvent = dateData.toLocaleString('ms-MY', {
                        weekday: 'long',
                    });
                }
                this.eventStartTime = formatTime(this.eventData.startTime);
                this.eventEndTime = formatTime(this.eventData.endTime);
            },
            error: (err) => {
                this.errorOnEvent = true;
                if (localStorage.getItem('language') == 'en') {
                    this.toastr.error(err.error?.message);
                } else {
                    if ((err.error.message = `No record found`)) {
                        this.toastr.error(`Tiada rekod dijumpai`);
                    } else {
                        this.toastr.error(`Id acara tiada`);
                    }
                }
            },
        });
    }

    getDateWithFormat(data, locale) {
        return data.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    get f() {
        return this.signInForm.controls;
    }

    getErrors(input: any, inputName?: any): Array<any> {
        let errors = [];

        if (
            this.f[input].touched ||
            this.submitted ||
            this.showErrorsOnSubmit
        ) {
            if (this.f[input].errors?.required) {
                errors.push(inputName + ' ' + this.isRequired);
            }
            if (this.f[input].errors?.pattern) {
                errors.push(inputName + ' ' + this.patternMatch);
            }
        }
        return errors;
    }

    onCheckCaptcha(captchaResponse: string) {
        this.captchaResolved =
            captchaResponse && captchaResponse.length > 0 ? true : false;
        this.captchaRes = captchaResponse;
    }

    onForgotPassword() {
        this.router.navigate(['/forgot-password']);
    }

    onSignUp() {
        let navigationExtras = {};
        if (this.calendarEventURL) {
            navigationExtras['queryParams'] = {
                calendarEventURL: this.calendarEventURL,
            };
        } else if (this.brpSourceUrl) {
            let utmSource = getUtmSourceBRPURL(this.brpSourceUrl);
            navigationExtras['queryParams'] = {
                brpSourceUrl: `/business-financing?utm_source=${utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`,
            };
        }
        this.router.navigate(['/sign-up'], navigationExtras);
    }

    onSignIn(): void {
        this.showErrorsOnSubmit = true;

        const loginDetails = {
            username: this.signInForm.value.username,
            password: this.signInForm.value.password,
            captcharesponse: this.captchaRes,
        };

        if (this.signInForm.valid) {
            this.submitted = true;
            this.ngxSpinner.show();
            this.authService.userLogin(loginDetails).subscribe({
                next: (res) => {
                    const secretKey = `${Math.floor(Date.now() / 1000)}${
                        environment.SECRET_KEY
                    }`;
                    localStorage.setItem('cepn', btoa(secretKey));
                    localStorage.setItem('isAuthenticated', 'true');
                    let profile = JSON.stringify(res.profile);
                    localStorage.setItem(
                        'profile',
                        this.encryptionService.encryptData(
                            btoa(profile),
                            secretKey,
                        ),
                    );
                    localStorage.removeItem('logout-event');
                    localStorage.setItem('accessToken', res.accessToken);
                    localStorage.setItem('idToken', res.idToken);
                    localStorage.setItem('refreshToken', res.refreshToken);
                    if (this.fromIDA) {
                        localStorage.setItem('fromIDA', 'true');
                    }
                    this.ngxSpinner.hide();

                    if (this.calendarEventURL) {
                        this.router.navigateByUrl(this.calendarEventURL);
                    } else if (this.brpSourceUrl) {
                        this.router.navigateByUrl(this.brpSourceUrl);
                    } else {
                        const secretEncKey = atob(localStorage.getItem('cepn'));
                        let profile = JSON.parse(
                            atob(
                                this.encryptionService.decryptData(
                                    localStorage.getItem('profile'),
                                    secretEncKey,
                                ),
                            ),
                        );
                        // nav to /profile when sms is unverified/verified
                        if (this.returnURL) {
                            this.router.navigate(['/profile'], {
                                queryParams: {
                                    financingApp: `true`,
                                },
                            });
                        } else {
                            this.router.navigate(['/profile']);
                        }
                    }
                },
                error: (err) => {
                    if (
                        err?.error?.code == 400 &&
                        err?.error?.reason === 'NotAuthorizedException' &&
                        err?.error?.message !==
                            'Incorrect username or password.'
                    ) {
                        this.toastr.error(
                            this.translate.instant('LOGIN.USER_DISABLED'),
                        );
                    }
                    this.recaptchaRef.reset();
                    this.ngxSpinner.hide();
                    this.submitted = false;
                    let errorMessage = err.error.message;
                    if (
                        errorMessage ==
                        "2 validation errors detected: Value at 'userAlias' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+; Value at 'userName' failed to satisfy constraint: Member must satisfy regular expression pattern: [\\p{L}\\p{M}\\p{S}\\p{N}\\p{P}]+"
                    ) {
                        if (localStorage.getItem('language') == 'bm') {
                            this.toastr.error(
                                'ID Pengguna tidak boleh mengandungi jarak',
                            );
                        } else {
                            this.toastr.error('Username cannot contain space');
                        }
                    } else if (
                        errorMessage == 'Incorrect username or password.'
                    ) {
                        if (localStorage.getItem('language') == 'bm') {
                            this.toastr.error(
                                'Nama pengguna atau kata laluan tidak tepat.',
                            );
                        } else {
                            this.toastr.error(errorMessage);
                        }
                    } else if (
                        errorMessage ==
                        'Account not verified. Please verify your email before logging in.'
                    ) {
                        this.openAccountUnverifiedDialog({
                            username: loginDetails.username,
                        });
                    } else {
                        let reason = err?.error?.reason;
                        if (reason == 'UserNotConfirmedException') {
                            if (localStorage.getItem('language') == 'bm') {
                                this.toastr.error(
                                    'Pengguna tidak dapat disahkan.',
                                );
                            } else {
                                this.toastr.error('User cannot be confirmed');
                            }
                            let userData = {
                                username: this.signInForm.value.username,
                            };
                            this.router.navigate(['/resend-code'], {
                                state: {
                                    userDetails: userData,
                                },
                            });
                        }
                    }
                },
            });
        } else {
            return;
        }
    }

    getLocalStorageValue(x: any) {
        return localStorage.getItem(x);
    }

    switchLanguage(language: string) {
        CHANGE_LANGUAGE(language, this.router.url, this.translate);
        this._translate();
        this.selectedLanguage = localStorage.getItem('language');
    }

    openDialog() {
        this.dialog.open(UserDisabledPopupComponent, {
            width: '100%',
            maxWidth: '450px',
            height: '250px',
            panelClass: 'force-password-popup',
        });
    }

    openAccountUnverifiedDialog(userDetails: { username: string }) {
        this.dialog.open(AccountUnverifiedModalComponent, {
            width: 'auto',
            maxWidth: '90vw',
            panelClass: 'account-unverified-dialog',
            data: {
                userDetails,
            },
        });
    }
}
